import { useState, useId, type KeyboardEvent } from 'react';

interface VideoTitleHeaderProps {
  title: string;
  filename: string;
  titleSource?: 'caption' | 'transcript' | 'filename' | 'manual' | null;
  onSave: (title: string) => void | Promise<void>;
  saving?: boolean;
  error?: Error | null;
}

export function VideoTitleHeader({
  title,
  filename,
  titleSource,
  onSave,
  saving,
  error,
}: VideoTitleHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputId = useId();
  const dirty = draft !== title;

  const handleSave = async () => {
    await onSave(draft);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(title);
    setEditing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (dirty && !saving) handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleReset = async () => {
    setDraft(title);
    await onSave('');
    setEditing(false);
  };

  return (
    <div className="space-y-1">
      {editing ? (
        <div className="space-y-2">
          <label htmlFor={inputId} className="sr-only">
            Video title
          </label>
          <input
            id={inputId}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Video title"
            className="w-full max-w-2xl rounded-md border border-gray-300 px-3 py-2 text-xl font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              className="text-sm px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="text-sm px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            {titleSource === 'manual' && (
              <button
                type="button"
                onClick={handleReset}
                disabled={saving}
                className="text-xs text-gray-500 hover:text-gray-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset to auto title
              </button>
            )}
          </div>
          {error && <div className="text-xs text-red-600">{error.message}</div>}
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <h1 className="text-2xl font-bold text-gray-900 break-words">{title}</h1>
          <button
            onClick={() => setEditing(true)}
            aria-label="Edit video title"
            className="text-sm text-gray-500 hover:text-gray-900 underline mt-1"
          >
            Edit
          </button>
        </div>
      )}
      <div className="font-mono text-sm text-gray-500">{filename}</div>
    </div>
  );
}
